// Sidebar wrapper — assembles header (h1 + ws-status + last-updated),
// kanban toggle button, project selector, targets nav, and footer
// (user-info + logout). Mirrors the legacy stbarts DOM
// (index.html L1681-1707) verbatim so the legacy CSS applies directly.
//
// The collapse/expand toggle is wired via the local `collapsed` signal.
//
// Phase 10A→F rewrite — replaces the alpha.10 TopBar + grid layout pattern.

import { Show, createSignal } from 'solid-js';
import { actions, stores } from '../state/store';
import { wsConnectionStatusSignal } from '../ws/client';
import { ProjectSelector } from './ProjectSelector';
import { TargetsSidebar } from './TargetsSidebar';

function formatLastUpdated(): string {
  // Legacy parity: the header timestamp is the date of the most recent
  // CAPTURE (metadata.lastUpdated), not the most recent remark. That is the
  // "when was this target last screenshot-ed" indicator a reviewer cares
  // about — remarks happen later but the baseline is the capture date.
  const captures = stores.state.captures;
  if (!captures.length) return '';
  const latest = captures.reduce(
    (acc, c) => Math.max(acc, c.capturedAt),
    0,
  );
  // Legacy stbarts: "M/D/YYYY HH:MM AM/PM" (locale en-US, no zero-pad on month/day).
  return new Date(latest).toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function Sidebar() {
  const [collapsed, setCollapsed] = createSignal(false);
  const wsStatus = () => {
    const s = wsConnectionStatusSignal();
    return s === 'connected' ? 'connected' : 'disconnected';
  };
  const wsTitle = () => {
    const s = wsConnectionStatusSignal();
    if (s === 'connected') return 'Synchronisation temps réel active';
    if (s === 'connecting') return 'Connexion WebSocket…';
    if (s === 'reconnecting') return 'Reconnexion WebSocket…';
    if (s === 'disconnected') return 'WebSocket déconnecté';
    return 'WebSocket inactif';
  };

  return (
    <div class={`sidebar ${collapsed() ? 'collapsed' : ''}`} id="sidebar">
      <div
        class="sidebar-tab"
        title={collapsed() ? 'Ouvrir le panneau' : 'Replier le panneau'}
        onClick={() => setCollapsed((v) => !v)}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </div>

      <div class="sidebar-header">
        <h1>
          SB Screenshots
          <span class={`ws-status ${wsStatus()}`} title={wsTitle()}></span>
        </h1>
        <div class="last-updated">{formatLastUpdated()}</div>
      </div>

      <div class="sidebar-kanban">
        <button
          type="button"
          class={stores.state.viewMode === 'kanban' ? 'active' : ''}
          title="Kanban view (K)"
          onClick={() =>
            actions.setViewMode(
              stores.state.viewMode === 'kanban' ? 'compare' : 'kanban',
            )
          }
        >
          📋 Kanban
        </button>
      </div>

      <nav class="sidebar-nav" id="sidebarNav">
        <ProjectSelector />
        <TargetsSidebar />
      </nav>

      <div class="sidebar-footer">
        <Show when={stores.auth.email}>
          <div class="login-user">
            <div class="user-info">
              <span class="username">
                {stores.auth.email?.split('@')[0] ?? ''}
              </span>
              <span class="email">{stores.auth.email}</span>
            </div>
            <button
              type="button"
              class="logout-btn"
              onClick={() => void actions.logout()}
            >
              Déconnexion
            </button>
          </div>
        </Show>
      </div>
    </div>
  );
}
